/**
 * MRT Filter Service
 * Convierte filtros de Material-React-Table a expresiones Prisma para GraphQL.
 *
 * Operadores soportados por tipo:
 * - string: contains, startsWith, endsWith, notContains, equal, notEqual
 * - number: equal, notEqual, lt, lte, gt, gte
 * - date: day, year, yearMonth, fromTo, empty, notEmpty (estos dos, sin valor)
 * - calendarDate (un date, día de calendario "AAAA-MM-DD"): los mismos, sin zona ni hora
 * - boolean: yes, no, all
 */

// Los filtros de fecha viven en un núcleo único, compartido con filterService
// (el traductor del ListStore). Antes estaban duplicados acá y allá, y derivaron.
import {
  dayFilter,
  yearFilter,
  yearMonthFilter,
  fromToFilter,
  calendarDayFilter,
  calendarYearFilter,
  calendarYearMonthFilter,
  calendarFromToFilter,
  type PrismaFilterExpression,
} from "./dateFilterCore";
export type { PrismaFilterExpression } from "./dateFilterCore";

// 'date' es una fecha con hora (un dateTime): instantes en la zona del idioma.
// 'calendarDate' es un date: el texto del día, sin zona ni hora.
export type ColumnType = 'string' | 'number' | 'date' | 'calendarDate' | 'boolean';

export type StringOperator = 'contains' | 'startsWith' | 'endsWith' | 'notContains' | 'equal' | 'notEqual';
export type NumberOperator = 'equal' | 'notEqual' | 'lt' | 'lte' | 'gt' | 'gte';
export type DateOperator = 'day' | 'year' | 'yearMonth' | 'fromTo' | 'empty' | 'notEmpty';
export type BooleanOperator = 'yes' | 'no' | 'all';

export type FilterOperator = StringOperator | NumberOperator | DateOperator | BooleanOperator;

/**
 * Los operadores que no llevan valor: "vacío" y "no vacío" de las fechas (la
 * columna nula o no nula). Un filtro con uno de estos vale aunque el valor esté
 * vacío; con los demás, un valor vacío es "sin filtro".
 */
const VALUELESS_OPERATORS = new Set<FilterOperator>(['empty', 'notEmpty']);

export function operatorNeedsValue(operator: FilterOperator): boolean {
  return !VALUELESS_OPERATORS.has(operator);
}

/**
 * ¿Se puede usar este operador sobre esta columna? "Vacío" y "No vacío" solo en
 * una fecha que puede ser nula: en una obligatoria (createdAt_, por ejemplo)
 * nunca hay un vacío y el server rechaza la pregunta (`equals: null` sobre una
 * columna que no admite null). `nullable` lo pone el generador; sin él, no se
 * ofrecen. Los demás operadores no dependen de eso.
 */
export function isOperatorAvailable(columnType: ColumnType, operator: FilterOperator, nullable?: boolean): boolean {
  if (operatorNeedsValue(operator)) return true;
  return (columnType === 'date' || columnType === 'calendarDate') && nullable === true;
}

/** El texto de quitar un filtro: el de la X de la celda y el del DataGrid son uno solo. */
export const CLEAR_FILTER_LABEL: Record<'es' | 'en' | 'pt', string> = {
  es: 'Limpiar filtro',
  en: 'Clear filter',
  pt: 'Limpar filtro',
};

export interface MRTFilter {
  columnName: string;
  columnType: ColumnType;
  operator: FilterOperator;
  value: string;
}

/**
 * Información de subFields para campos de referencia.
 * Permite filtrar por los campos que componen el displayId de la entidad referenciada.
 */
export type SubFilters = Record<string, { subFields: string[]; multi: boolean } | undefined>;

/** Los operadores de una fecha: los mismos con hora ('date') y sin hora ('calendarDate'). */
const DATE_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'day', label: 'Día' },
  { value: 'yearMonth', label: 'Mes' },
  { value: 'year', label: 'Año' },
  { value: 'fromTo', label: 'Rango' },
  { value: 'empty', label: 'Vacío' },
  { value: 'notEmpty', label: 'No vacío' },
];

/**
 * Operadores disponibles por tipo de columna
 */
export const operatorsByType: Record<ColumnType, { value: FilterOperator; label: string }[]> = {
  string: [
    { value: 'contains', label: 'Contiene' },
    { value: 'startsWith', label: 'Comienza con' },
    { value: 'endsWith', label: 'Termina con' },
    { value: 'notContains', label: 'No contiene' },
    { value: 'equal', label: 'Igual a' },
    { value: 'notEqual', label: 'Diferente de' },
  ],
  number: [
    { value: 'equal', label: '=' },
    { value: 'notEqual', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
  ],
  date: DATE_OPERATORS,
  calendarDate: DATE_OPERATORS,
  boolean: [
    { value: 'all', label: 'Todos' },
    { value: 'yes', label: 'Sí' },
    { value: 'no', label: 'No' },
  ],
};

/**
 * Operador por defecto para cada tipo
 */
export const defaultOperatorByType: Record<ColumnType, FilterOperator> = {
  string: 'contains',
  number: 'equal',
  date: 'day',
  calendarDate: 'day',
  boolean: 'all',
};

/**
 * Convierte un filtro MRT a expresión Prisma
 */
export function mrtFilterToPrisma(
  columnName: string,
  columnType: ColumnType,
  operator: FilterOperator,
  value: string,
  timeZone: string = 'America/Buenos_Aires',
  subFilters?: SubFilters
): PrismaFilterExpression {
  // Vacío / no vacío: sin valor (si quedó uno de antes, no cuenta).
  if ((columnType === 'date' || columnType === 'calendarDate') && !operatorNeedsValue(operator)) {
    return dateFilterToPrisma(columnName, operator as DateOperator, '', timeZone);
  }
  if (!value || value.trim() === '') {
    return null;
  }

  const trimmedValue = value.trim();

  switch (columnType) {
    case 'string':
      return stringFilterToPrisma(columnName, operator as StringOperator, trimmedValue, subFilters);
    case 'number':
      return numberFilterToPrisma(columnName, operator as NumberOperator, trimmedValue);
    case 'date':
      return dateFilterToPrisma(columnName, operator as DateOperator, trimmedValue, timeZone);
    case 'calendarDate':
      return calendarDateFilterToPrisma(columnName, operator as DateOperator, trimmedValue);
    case 'boolean':
      return booleanFilterToPrisma(columnName, operator as BooleanOperator);
    default:
      return null;
  }
}

/**
 * Filtros de string
 */
function stringFilterToPrisma(
  columnName: string,
  operator: StringOperator,
  value: string,
  subFilters?: SubFilters
): PrismaFilterExpression {
  const mode = { mode: 'insensitive' };
  const subFilterInfo = subFilters?.[columnName];

  // Si el campo tiene subFilters (es una referencia), construir WHERE anidado
  if (subFilterInfo && subFilterInfo.subFields.length > 0) {
    return buildReferenceFilter(columnName, operator, value, subFilterInfo, mode);
  }

  // Campo escalar: filtro directo
  switch (operator) {
    case 'contains':
      return { [columnName]: { contains: value, ...mode } };
    case 'startsWith':
      return { [columnName]: { startsWith: value, ...mode } };
    case 'endsWith':
      return { [columnName]: { endsWith: value, ...mode } };
    case 'notContains':
      return { NOT: { [columnName]: { contains: value, ...mode } } };
    case 'equal':
      return { [columnName]: { equals: value } };
    case 'notEqual':
      return { NOT: { [columnName]: { equals: value } } };
    default:
      return null;
  }
}

/**
 * Construye filtro para campos de referencia usando los subFields del displayId
 */
function buildReferenceFilter(
  columnName: string,
  operator: StringOperator,
  value: string,
  subFilterInfo: { subFields: string[]; multi: boolean },
  mode: { mode: string }
): PrismaFilterExpression {
  const { subFields, multi } = subFilterInfo;

  // Construir expresión para cada subField
  const subFieldExpressions = subFields.map(subField => {
    const fieldPath = subField.split('.');
    let filterValue: Record<string, unknown>;

    switch (operator) {
      case 'contains':
        filterValue = { contains: value, ...mode };
        break;
      case 'startsWith':
        filterValue = { startsWith: value, ...mode };
        break;
      case 'endsWith':
        filterValue = { endsWith: value, ...mode };
        break;
      case 'notContains':
        // Para notContains en referencia, aplicamos NOT a cada subField
        filterValue = { contains: value, ...mode };
        break;
      case 'equal':
        filterValue = { equals: value };
        break;
      case 'notEqual':
        filterValue = { equals: value };
        break;
      default:
        filterValue = { contains: value, ...mode };
    }

    // Construir objeto anidado para paths como "author.name"
    let result: Record<string, unknown> = filterValue;
    for (let i = fieldPath.length - 1; i >= 0; i--) {
      result = { [fieldPath[i]]: result };
    }
    return result;
  });

  // Combinar subFields con OR (buscar en cualquiera de los campos del displayId)
  const combinedExpression = subFieldExpressions.length === 1
    ? subFieldExpressions[0]
    : { OR: subFieldExpressions };

  // Para relaciones many-to-many, usar .some()
  const relationExpression = multi
    ? { [columnName]: { some: combinedExpression } }
    : { [columnName]: combinedExpression };

  // Manejar operadores negativos
  if (operator === 'notContains' || operator === 'notEqual') {
    return { NOT: relationExpression };
  }

  return relationExpression;
}

/**
 * Filtros numéricos
 */
function numberFilterToPrisma(
  columnName: string,
  operator: NumberOperator,
  value: string
): PrismaFilterExpression {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return null;
  }

  switch (operator) {
    case 'equal':
      return { [columnName]: { equals: numValue } };
    case 'notEqual':
      return { NOT: { [columnName]: { equals: numValue } } };
    case 'lt':
      return { [columnName]: { lt: numValue } };
    case 'lte':
      return { [columnName]: { lte: numValue } };
    case 'gt':
      return { [columnName]: { gt: numValue } };
    case 'gte':
      return { [columnName]: { gte: numValue } };
    default:
      return null;
  }
}

/**
 * Filtros de fecha
 */
function dateFilterToPrisma(
  columnName: string,
  operator: DateOperator,
  value: string,
  timeZone: string
): PrismaFilterExpression {
  switch (operator) {
    case 'day':
      return dayFilter(columnName, value, timeZone);
    case 'year':
      return yearFilter(columnName, value, timeZone);
    case 'yearMonth':
      return yearMonthFilter(columnName, value, timeZone);
    case 'fromTo':
      return fromToFilter(columnName, value, timeZone);
    case 'empty':
      return { [columnName]: { equals: null } };
    case 'notEmpty':
      return { [columnName]: { not: null } };
    default:
      return null;
  }
}

/**
 * Filtros de un date (día de calendario, "AAAA-MM-DD"): sin zona ni hora
 * (dateFilterCore). Vacío y no vacío los resuelve dateFilterToPrisma, como en una
 * fecha con hora.
 */
function calendarDateFilterToPrisma(
  columnName: string,
  operator: DateOperator,
  value: string
): PrismaFilterExpression {
  switch (operator) {
    case 'day':
      return calendarDayFilter(columnName, value);
    case 'year':
      return calendarYearFilter(columnName, value);
    case 'yearMonth':
      return calendarYearMonthFilter(columnName, value);
    case 'fromTo':
      return calendarFromToFilter(columnName, value);
    default:
      return null;
  }
}

/**
 * Filtros booleanos
 */
function booleanFilterToPrisma(
  columnName: string,
  operator: BooleanOperator
): PrismaFilterExpression {
  switch (operator) {
    case 'yes':
      return { [columnName]: { equals: true } };
    case 'no':
      return { [columnName]: { equals: false } };
    case 'all':
      return undefined; // Sin filtro
    default:
      return null;
  }
}

/**
 * Convierte un array de filtros MRT a una expresión Prisma combinada (AND)
 */
export function mrtFiltersToPrismaWhere(
  filters: MRTFilter[],
  timeZone: string = 'America/Buenos_Aires',
  subFilters?: SubFilters
): PrismaFilterExpression {
  const expressions = filters
    .map(f => mrtFilterToPrisma(f.columnName, f.columnType, f.operator, f.value, timeZone, subFilters))
    .filter((expr): expr is Record<string, unknown> => expr !== null && expr !== undefined);

  if (expressions.length === 0) {
    return undefined;
  }

  if (expressions.length === 1) {
    return expressions[0];
  }

  return { AND: expressions };
}

/**
 * Obtiene las etiquetas de operadores en el idioma especificado
 */
export function getOperatorLabels(language: 'es' | 'en' | 'pt' = 'es'): Record<ColumnType, { value: FilterOperator; label: string }[]> {
  const labels: Record<string, Record<FilterOperator, string>> = {
    es: {
      contains: 'Contiene',
      startsWith: 'Comienza con',
      endsWith: 'Termina con',
      notContains: 'No contiene',
      equal: 'Igual a',
      notEqual: 'Diferente de',
      lt: 'Menor que',
      lte: 'Menor o igual',
      gt: 'Mayor que',
      gte: 'Mayor o igual',
      day: 'Día',
      year: 'Año',
      yearMonth: 'Mes',
      fromTo: 'Rango',
      empty: 'Vacío',
      notEmpty: 'No vacío',
      yes: 'Sí',
      no: 'No',
      all: 'Todos',
    },
    en: {
      contains: 'Contains',
      startsWith: 'Starts with',
      endsWith: 'Ends with',
      notContains: 'Not contains',
      equal: 'Equals',
      notEqual: 'Not equals',
      lt: 'Less than',
      lte: 'Less or equal',
      gt: 'Greater than',
      gte: 'Greater or equal',
      day: 'Day',
      year: 'Year',
      yearMonth: 'Month',
      fromTo: 'Range',
      empty: 'Empty',
      notEmpty: 'Not empty',
      yes: 'Yes',
      no: 'No',
      all: 'All',
    },
    pt: {
      contains: 'Contém',
      startsWith: 'Começa com',
      endsWith: 'Termina com',
      notContains: 'Não contém',
      equal: 'Igual a',
      notEqual: 'Diferente de',
      lt: 'Menor que',
      lte: 'Menor ou igual',
      gt: 'Maior que',
      gte: 'Maior ou igual',
      day: 'Dia',
      year: 'Ano',
      yearMonth: 'Mês',
      fromTo: 'Intervalo',
      empty: 'Vazio',
      notEmpty: 'Não vazio',
      yes: 'Sim',
      no: 'Não',
      all: 'Todos',
    },
  };

  const langLabels = labels[language] || labels.es;

  return {
    string: operatorsByType.string.map(op => ({ ...op, label: langLabels[op.value] || op.label })),
    number: operatorsByType.number.map(op => ({
      ...op,
      label: ['equal', 'notEqual', 'lt', 'lte', 'gt', 'gte'].includes(op.value)
        ? op.label // Mantener símbolos =, ≠, >, etc.
        : langLabels[op.value] || op.label
    })),
    date: operatorsByType.date.map(op => ({ ...op, label: langLabels[op.value] || op.label })),
    calendarDate: operatorsByType.calendarDate.map(op => ({ ...op, label: langLabels[op.value] || op.label })),
    boolean: operatorsByType.boolean.map(op => ({ ...op, label: langLabels[op.value] || op.label })),
  };
}
